import { useCallback, useRef, useState } from 'react'
import { ArrowUp, Mic } from 'lucide-react'
import { useSetupVoiceInput } from '../../hooks/useSetupVoiceInput'

function SetupComposer({ onSend, disabled = false, placeholder: placeholderProp, suggestions = [] }) {
  const [value, setValue] = useState('')
  const usedVoiceRef = useRef(false)
  const {
    statusLabel,
    voiceStatus,
    interimTranscript,
    voiceError,
    isListening,
    startVoice: rawStartVoice,
    stopVoice,
  } = useSetupVoiceInput({
    value,
    onChange: setValue,
    disabled,
  })

  const startVoice = (...args) => {
    usedVoiceRef.current = true
    return rawStartVoice(...args)
  }

  const isVoiceBusy = voiceStatus === 'requesting_microphone' || voiceStatus === 'listening' || voiceStatus === 'transcribing'

  const handleSubmit = (event) => {
    event.preventDefault()
    if (disabled) return
    const message = value.trim()
    if (!message) return
    onSend(message, { fromVoice: usedVoiceRef.current })
    usedVoiceRef.current = false
    setValue('')
    if (textareaRef.current) textareaRef.current.style.height = 'auto'
  }

  const textareaRef = useRef(null)
  const autoResize = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`
  }, [])

  const handleTextareaKeyDown = (event) => {
    if (event.key !== 'Enter' || event.shiftKey) return
    event.preventDefault()
    if (disabled) return
    if (isListening) {
      stopVoice(false)
      return
    }
    if (isVoiceBusy) return
    event.currentTarget.form?.requestSubmit()
  }

  const hasText = value.trim().length > 0

  return (
    <form
      data-debug-setup-composer
      onSubmit={handleSubmit}
      className="border-t border-gray-100 bg-white px-4 py-3 sm:px-6"
    >
      {/* Suggestion chips — only on first message */}
      {suggestions.length > 0 && !disabled && !hasText && (
        <div className="flex flex-wrap gap-2 mb-3">
          {suggestions.map((chip) => (
            <button
              key={chip}
              type="button"
              onClick={() => { setValue(chip) }}
              className="rounded-full border border-gray-200 bg-white px-3.5 py-1.5 text-[13px] text-gray-500 transition-all hover:border-gray-300 hover:text-gray-700 hover:shadow-sm"
            >
              {chip}
            </button>
          ))}
        </div>
      )}

      {/* Voice status — always reserves height so the composer never shifts when listening */}
      <div className="h-5 flex items-center gap-2 mb-1">
        {statusLabel && (
          <>
            {isListening && (
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
              </span>
            )}
            <span className="text-xs text-gray-400">{statusLabel}</span>
          </>
        )}
      </div>

      {/* Input row */}
      <div className="flex items-end gap-2">
        <div className="flex-1 rounded-2xl border border-gray-200 bg-gray-50/50 px-4 py-2.5 focus-within:border-gray-300 focus-within:bg-white transition-colors">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(event) => { setValue(event.target.value); autoResize() }}
            onKeyDown={handleTextareaKeyDown}
            placeholder={placeholderProp || 'Tell me about your meeting...'}
            rows={1}
            className="min-h-[1.5rem] max-h-[200px] w-full resize-none overflow-y-auto border-0 bg-transparent p-0 text-[15px] text-gray-900 outline-none focus:ring-0 placeholder:text-gray-400"
          />
        </div>
        <button
          type="button"
          onClick={isListening ? () => stopVoice(false) : startVoice}
          disabled={disabled || (isVoiceBusy && !isListening)}
          className={`shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-full transition-all ${
            isListening
              ? 'bg-red-500 text-white shadow-sm hover:bg-red-600'
              : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
          } disabled:cursor-not-allowed disabled:opacity-40`}
          aria-label={isListening ? 'Stop listening' : 'Use voice'}
        >
          <Mic className="h-[18px] w-[18px]" />
        </button>
        <button
          type="submit"
          disabled={disabled || isVoiceBusy || !hasText}
          className={`shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-full transition-all ${
            hasText && !disabled
              ? 'bg-gray-900 text-white shadow-sm hover:bg-gray-800'
              : 'bg-gray-200 text-gray-400'
          } disabled:cursor-not-allowed`}
          aria-label="Send message"
        >
          <ArrowUp className="h-[18px] w-[18px]" strokeWidth={2.5} />
        </button>
      </div>

      {/* Voice transcript */}
      {interimTranscript ? (
        <p className="mt-2 text-xs text-gray-400">"{interimTranscript}"</p>
      ) : null}

      {/* Voice error */}
      {voiceError ? (
        <p className="mt-2 rounded-lg bg-amber-50 px-3 py-1.5 text-xs text-amber-700">
          {voiceError}
        </p>
      ) : null}
    </form>
  )
}

export default SetupComposer
